import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useCanBlockNumber } from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import {
  CopyLinkToClipboard,
  Delete,
  MarkAsUnread,
  MoveFax,
  NewFax,
  ResendFax,
} from '~/components/compositional/QuickActions'
import { ResponsivePage } from '~/components/layout/ResponsivePage'
import { EmptyDetailView } from '~/components/shared/EmptyDetailView'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { getNeighborRowFocusTarget } from '~/components/shared/ScrollingList'
import { ToggleBlockedNumberButton } from '~/components/specialized/BlockedNumber/ToggleBlockedNumberButton'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useClipboard } from '~/hooks/useClipboard'
import { PAGE_HEADING_ID } from '~/hooks/useRouteFocusAndTitle'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { useFaxViewModel } from '~/pages/Fax/Fax.viewmodel'
import { FaxDetailHeader, FaxDetailView } from '~/pages/Fax/FaxDetailView'
import { FaxListView } from '~/pages/Fax/FaxListView'
import { useFaxPageQueryParams } from '~/pages/Fax/hooks/useFaxPageQueryParams'
import { useNewFaxViewModel } from '~/pages/Home/Modals/NewFaxModal/viewModels/NewFaxViewModel'
import { useAutoRead } from '~/queries/faxQueries'
import { AnalyticsNamespace, FaxAnalyticsEvents } from '~/types/Analytics'
import { getShareableWebAppUrl } from '~/utils/shareableUrl'

import { useDebouncedCallback } from '@mangovoice/core'
import { FaxInstance, FaxStatus } from '@mangovoice/types'

const TOGGLE_UNREAD_DEBOUNCE_MS = 200

export const FaxPage = () => {
  const { track } = useAnalytics(AnalyticsNamespace.FAXING)
  const { state, actions, utils } = useFaxViewModel()
  const { actions: newFaxActions } = useNewFaxViewModel()
  const { copyToClipboard } = useClipboard()
  const { toastSuccess, toastError } = useToastAlert()
  const { setPage } = useFaxPageQueryParams()
  const navigate = useNavigate()
  const {
    faxModal: [faxModalState, setFaxModalState],
    confirmModal: [, setConfirmModalState],
    moveFaxModal: [, setMoveFaxModalState],
  } = useModalState()
  const { t } = useTranslation()
  const announce = useAnnounce({ clearWhenChanged: state.selectedFaxId })

  const canBlockNumber = useCanBlockNumber(
    state.selectedFaxInstance?.from_phone_number.phone_number,
  )

  const getExtensionName = ((
    fax: FaxInstance | undefined,
  ): string | undefined => {
    if (!fax) {
      return undefined as undefined
    } else {
      return fax.extension.name
    }
  }) as ((_: FaxInstance) => string) &
    ((_: FaxInstance | undefined) => string | undefined)

  const handleResendFaxClicked = () => {
    if (!state.selectedFaxInstance) {
      return
    }

    setConfirmModalState({
      title: t('faxPage.confirmResend.title'),
      message: t('faxPage.confirmResend.message'),
      confirmText: t('faxPage.confirmResend.button'),
      cancelText: t('faxPage.confirmResend.cancel'),
      closeAfterConfirm: true,
      isDestructive: false,
      onConfirm: () => {
        if (state.selectedFaxId) {
          newFaxActions.resendFax(state.selectedFaxId)
        }
      },
    })
    track(FaxAnalyticsEvents.QuickActionResend)
  }

  const handleMoveFaxClicked = () => {
    setMoveFaxModalState({
      open: true,
    })
  }

  const handleNewFaxClicked = () => {
    setFaxModalState({
      ...faxModalState,
      open: true,
      number: utils.getNumber(state.selectedFaxInstance),
    })
    track(FaxAnalyticsEvents.QuickActionReply)
  }

  const faxesRef = useRef(state.faxes)
  faxesRef.current = state.faxes

  const getFocusTargetAfterDelete = (faxId: number) => {
    const deletedIndex = faxesRef.current.findIndex((fax) => fax.id === faxId)
    return (
      getNeighborRowFocusTarget(document, deletedIndex) ??
      document.getElementById(PAGE_HEADING_ID)
    )
  }

  const handleDeleteClicked = () => {
    const faxId = state.selectedFaxId
    setConfirmModalState({
      title: t('faxPage.confirmDelete.title'),
      message: t('faxPage.confirmDelete.message'),
      confirmText: t('faxPage.confirmDelete.button'),
      cancelText: t('faxPage.confirmDelete.cancel'),
      closeAfterConfirm: true,
      isDestructive: true,
      getFocusTargetAfterConfirm: faxId
        ? () => getFocusTargetAfterDelete(faxId)
        : undefined,
      onConfirm: () => {
        if (state.selectedFaxId) {
          actions.deleteFax(state.selectedFaxId)
          // THOMDO: Verify this clears the search params
          navigate(ApplicationRoutes.FAX)
        }
      },
    })
  }

  const handleCopyLink = () => {
    track(FaxAnalyticsEvents.LinkCopied)
    copyToClipboard(
      getShareableWebAppUrl(),
      () => toastSuccess(t('faxPage.toast.copyLinkSuccess')),
      () => toastError(t('faxPage.toast.copyLinkError')),
    )
  }

  const toggleUnread = useDebouncedCallback(
    () =>
      actions.toggleUnread({
        onSuccess: (isUnread) =>
          announce(
            isUnread
              ? t('listItem.markedAsUnread', 'Marked as unread')
              : t('listItem.markedAsRead', 'Marked as read'),
          ),
      }),
    TOGGLE_UNREAD_DEBOUNCE_MS,
  )

  const { detailHeader: DetailHeader, detailView: DetailView } = useMemo(() => {
    let detailView: React.ReactNode
    let detailHeader: React.ReactNode
    const currentFax = state.selectedFaxInstance
    if (currentFax) {
      const isQueued = currentFax.status === FaxStatus.Pending
      const isReceived = currentFax.direction === 'inbound'
      const dateString =
        isQueued || !currentFax.sent_at
          ? currentFax.created_at
          : currentFax.sent_at
      const date = new Date(dateString)
      const statusPrefix = isQueued
        ? t('faxPage.status.queued')
        : isReceived
          ? t('faxPage.status.received')
          : t('faxPage.status.sent')
      const directionText = isReceived
        ? t('faxPage.direction.to')
        : t('faxPage.direction.from')
      const status = t('faxPage.detailsHeader.status', {
        statusPrefix,
        statusDate: date.toDateString(),
        statusTime: date.toLocaleTimeString(),
        directionText,
      })

      detailHeader = (
        <FaxDetailHeader
          name={utils.getNumber(currentFax)}
          timestamp={status}
          selectedVirtualFaxName={getExtensionName(currentFax)}
        />
      )

      detailView = (
        <FaxDetailView
          fileUrl={currentFax.file_url}
          fileName={currentFax.file_name}
          onPageChange={setPage}
        />
      )
    } else {
      // Empty State
      detailHeader = null
      detailView = <EmptyDetailView message={t('faxPage.emptyStateMessage')} />
    }

    return { detailHeader, detailView }
  }, [state.selectedFaxInstance?.id, state.faxPdfPageNumber])

  const QuickActionButtons = useMemo(
    () =>
      state.selectedFaxInstance && [
        <MarkAsUnread
          action={
            state.selectedFaxInstance.read_at ? 'mark_unread' : 'mark_read'
          }
          key="toggle-unread"
          onClick={toggleUnread}
        />,
        <CopyLinkToClipboard key="copy" onCopyLink={handleCopyLink} />,
        <NewFax key="fax" onClick={handleNewFaxClicked} />,
        <ResendFax key="resend" onClick={handleResendFaxClicked} />,
        ...(state.extensions.length > 1
          ? [<MoveFax key="move" onClick={handleMoveFaxClicked} />]
          : []),
        ...(canBlockNumber
          ? [
              <ToggleBlockedNumberButton
                key="toggle-blocked"
                phoneNumber={
                  state.selectedFaxInstance.from_phone_number.phone_number
                }
              />,
            ]
          : []),
        <Delete key="delete" onClick={handleDeleteClicked} />,
      ],
    [
      state.extensions.length,
      state.selectedFaxInstance?.id,
      state.selectedFaxInstance?.read_at,
    ],
  )

  useAutoRead(state.selectedFaxId || null, state.faxSearch, state.extensionIds)

  return (
    <>
      <ResponsivePage
        listPanel={
          <FaxListView state={state} actions={actions} utils={utils} />
        }
        header={DetailHeader}
        quickActionButtons={QuickActionButtons}
        detailPanel={DetailView}
        backgroundColor="var(--chakra-colors-bg-subtle)"
      />
    </>
  )
}
